import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, Plus, Copy, Archive, Users, Download, Ticket, Store } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export default function EventsDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [stallCounts, setStallCounts] = useState<Record<string, number>>({});
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [stallApplications, setStallApplications] = useState<any[]>([]);
  const [applicationsDialogOpen, setApplicationsDialogOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchEvents();
    }
  }, [user]);

  const fetchEvents = async () => {
    const { data: eventsData, error } = await supabase
      .from('events')
      .select('*')
      .order('event_date', { ascending: true });

    if (!error && eventsData) {
      setEvents(eventsData);
      
      // Fetch stall application counts for each event
      const { data: stallData } = await supabase
        .from('stall_applications')
        .select('event_id, status');

      if (stallData) {
        const counts: Record<string, number> = {};
        stallData.forEach(app => {
          if (!counts[app.event_id]) {
            counts[app.event_id] = 0;
          }
          counts[app.event_id]++;
        });
        setStallCounts(counts);
      }
    }
  };

  const handleViewApplications = async (event: any) => {
    setSelectedEvent(event);
    setApplicationsDialogOpen(true);
    
    const { data, error } = await supabase
      .from('stall_applications')
      .select('*')
      .eq('event_id', event.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error("Failed to load applications");
      console.error(error);
    } else {
      setStallApplications(data || []);
    }
  };

  const stalls = [
    { id: 1, name: "BBQ Stand", volunteers: 3, float: "£50", utilities: "Power, Water" },
    { id: 2, name: "Face Painting", volunteers: 2, float: "£20", utilities: "None" },
    { id: 3, name: "Book Stall", volunteers: 2, float: "£30", utilities: "None" },
  ];

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground">Events Dashboard</h1>
      </div>

      <Tabs defaultValue="events" className="w-full">
        <TabsList>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="stalls">Stalls</TabsTrigger>
          <TabsTrigger value="helpers">Helpers</TabsTrigger>
          <TabsTrigger value="ticketing">Ticketing</TabsTrigger>
          <TabsTrigger value="planner">Event Planner</TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Manage Events</CardTitle>
                  <CardDescription>Create, edit, and organize PTA events</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <Copy className="h-4 w-4 mr-2" />
                        Use Template
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Select Event Template</DialogTitle>
                        <DialogDescription>Choose a pre-configured event template</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div className="p-3 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
                          <h4 className="font-semibold">Summer Fair Template</h4>
                          <p className="text-sm text-muted-foreground">Includes BBQ, games, face painting</p>
                        </div>
                        <div className="p-3 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
                          <h4 className="font-semibold">Bake Sale Template</h4>
                          <p className="text-sm text-muted-foreground">Simple refreshments event</p>
                        </div>
                        <div className="p-3 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
                          <h4 className="font-semibold">Disco Night Template</h4>
                          <p className="text-sm text-muted-foreground">Evening entertainment event</p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        New Event
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Create New Event</DialogTitle>
                        <DialogDescription>Set up a new PTA event</DialogDescription>
                      </DialogHeader>
                      <form className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="event-name">Event Name</Label>
                          <Input id="event-name" placeholder="e.g., Summer Fair 2025" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="event-date">Date</Label>
                            <Input id="event-date" type="date" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="event-time">Time</Label>
                            <Input id="event-time" type="time" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event-location">Location</Label>
                          <Input id="event-location" placeholder="e.g., School Field" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event-goal">Link to Goal (Optional)</Label>
                          <Select>
                            <SelectTrigger id="event-goal">
                              <SelectValue placeholder="Select goal" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Playground Equipment</SelectItem>
                              <SelectItem value="2">Library Books</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event-description">Description</Label>
                          <Textarea id="event-description" placeholder="Event details..." rows={3} />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline">Cancel</Button>
                          <Button type="submit">Create Event</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stall Applications</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No events yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-primary" />
                            {event.name}
                          </div>
                        </TableCell>
                        <TableCell>{new Date(event.event_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant={event.status === "active" ? "default" : "secondary"}>
                            {event.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewApplications(event)}
                            className="flex items-center gap-2 hover:bg-accent"
                          >
                            <Store className="h-4 w-4 text-primary" />
                            <span className="font-medium">{stallCounts[event.id] || 0}</span>
                            <span className="text-sm text-muted-foreground">applications</span>
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm">Edit</Button>
                            <Button variant="outline" size="sm">
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <Archive className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stalls" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Reusable Stalls</CardTitle>
                  <CardDescription>Configure stall requirements and resources</CardDescription>
                </div>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stall
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stalls.map((stall) => (
                  <div key={stall.id} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold mb-2">{stall.name}</h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Volunteers Required</p>
                            <p className="font-medium">{stall.volunteers}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Float</p>
                            <p className="font-medium">{stall.float}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Utilities</p>
                            <p className="font-medium">{stall.utilities}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">Edit</Button>
                        <Button variant="outline" size="sm">Assign</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="helpers" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Helper Sign-Ups</CardTitle>
              <CardDescription>Manage volunteer registrations for events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">Summer Fair - Helpers</h4>
                      <p className="text-sm text-muted-foreground">12 volunteers signed up</p>
                    </div>
                    <Button variant="outline" size="sm">View All</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">Sarah Smith - BBQ</Badge>
                    <Badge variant="secondary">John Doe - Face Painting</Badge>
                    <Badge variant="secondary">Emma Wilson - Tickets</Badge>
                    <Badge variant="secondary">+9 more</Badge>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold mb-2">Sign-Up Link</h4>
                  <div className="flex gap-2">
                    <Input value="https://ptahub.app/event/summer-fair/signup" readOnly className="font-mono text-sm" />
                    <Button variant="outline">Copy</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ticketing" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Event Ticketing</CardTitle>
              <CardDescription>Manage named tickets and extras</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-semibold">Summer Fair Tickets</h4>
                      <p className="text-sm text-muted-foreground">Entry: £5.00 per person</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">48</p>
                      <p className="text-sm text-muted-foreground">Tickets sold</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h5 className="font-medium mb-2">Extras Available:</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 bg-muted/30 rounded">
                          <p className="text-sm font-medium">Hot Dog</p>
                          <p className="text-xs text-muted-foreground">£2.50 - 15 sold</p>
                        </div>
                        <div className="p-2 bg-muted/30 rounded">
                          <p className="text-sm font-medium">Drink</p>
                          <p className="text-xs text-muted-foreground">£1.00 - 22 sold</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-1" />
                        Attendee List
                      </Button>
                      <Button variant="outline" size="sm">
                        <Ticket className="h-4 w-4 mr-1" />
                        Manage Tickets
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="planner" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Event Planner</CardTitle>
              <CardDescription>Resource summary and planning overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-semibold mb-3">Summer Fair - June 15, 2025</h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-sm font-medium mb-2">Stalls (3)</h5>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• BBQ Stand</li>
                        <li>• Face Painting</li>
                        <li>• Book Stall</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="text-sm font-medium mb-2">Resources Required</h5>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Volunteers: 7</li>
                        <li>• Total Float: £100</li>
                        <li>• Utilities: Power, Water</li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium mb-2">Catering</h5>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-800">Catering Assigned</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">100+ items allocated</p>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium mb-2">Linked Goal</h5>
                      <Badge variant="outline">Playground Equipment</Badge>
                      <p className="text-sm text-muted-foreground mt-1">Target: £5,000</p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border flex gap-2">
                    <Button>
                      <Download className="h-4 w-4 mr-2" />
                      Export Plan PDF
                    </Button>
                    <Button variant="outline">Email Summary</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={applicationsDialogOpen} onOpenChange={setApplicationsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stall Applications - {selectedEvent?.name}</DialogTitle>
            <DialogDescription>
              {selectedEvent && new Date(selectedEvent.event_date).toLocaleDateString()} • {stallApplications.length} application{stallApplications.length !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {stallApplications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No applications yet for this event
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Applied</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stallApplications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell className="font-medium">{app.business_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{app.contact_name}</div>
                          <div className="text-muted-foreground">{app.business_email}</div>
                          <div className="text-muted-foreground">{app.contact_phone}</div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="text-sm line-clamp-2">{app.business_description}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          app.status === 'approved' ? 'default' : 
                          app.status === 'declined' ? 'destructive' : 
                          'secondary'
                        }>
                          {app.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(app.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
